import React, { useState, ReactNode, useMemo } from 'react';
import { Dialog } from '../Dialog';
import { useCampaignForm } from 'hooks';
import { Stack } from '@mui/material';
import { UploadedCreativePreview } from '../UploadedCreativePreview';
import { CreativeFile } from 'types';
import { isMediaSizeValid } from 'utils';

export const getCreativePreviewId = (index: number) => `creative-preview-${index}`;

type CreativeUploadDialogProps = {
  open: boolean;
  onClose: VoidFunction;
  warningMessage: ReactNode;
  creatives: CreativeFile[];
  allUploadedFilesNumber: number;
  setAllUploadedFilesNumber: React.Dispatch<React.SetStateAction<number>>;
  websiteIndex: number;
  onSave: (creatives: CreativeFile[]) => void;
  setCreatives: (creatives: CreativeFile[]) => void;
  isUploading?: boolean;
  isUploadDisabled?: boolean;
};

export const CreativeUploadDialog = ({
  open,
  onSave,
  onClose,
  creatives,
  setCreatives,
  warningMessage,
  websiteIndex,
  isUploading,
  isUploadDisabled,
  allUploadedFilesNumber,
  setAllUploadedFilesNumber,
}: CreativeUploadDialogProps) => {
  const { watch } = useCampaignForm();
  const [shouldValidate, setShouldValidate] = useState(false);

  const { websites } = watch();

  const deleteCreative = (index: number) => {
    const creativeFiles = creatives.filter((_, i) => i !== index);
    setAllUploadedFilesNumber(allUploadedFilesNumber - 1);

    if (!creativeFiles.length) {
      onClose();
    }

    setCreatives(creativeFiles);
  };

  const updateCreative = (index: number, creative: CreativeFile) =>
    setCreatives(creatives.map((c, i) => (i === index ? creative : c)));

  const isValid = useMemo(
    () =>
      creatives.every(
        (creative) => !!creative.creativeType && isMediaSizeValid(creative.file, creative.creativeType.maxFileSizeBytes)
      ),
    [creatives]
  );

  const saveCreative = () => {
    setShouldValidate(true);

    if (isValid) {
      onSave(creatives);
      setShouldValidate(false);
    } else {
      const firstInvalidIndex = creatives.findIndex(
        (creative) => !creative.creativeType || !isMediaSizeValid(creative.file, creative.creativeType.maxFileSizeBytes)
      );

      if (firstInvalidIndex !== -1) {
        const element = document.getElementById(`creative-preview-${firstInvalidIndex}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  const title = `Format prüfen und bestätigen für\n${websites[websiteIndex]?.url}`;
  const content = (
    <Stack gap={5}>
      {warningMessage}
      {creatives.map((creative, index) => (
        <UploadedCreativePreview
          id={getCreativePreviewId(index)}
          key={index}
          isEdit={false}
          creative={creative}
          isValid={isValid || !shouldValidate}
          onDelete={() => deleteCreative(index)}
          setCreative={(creative) => updateCreative(index, creative)}
        />
      ))}
    </Stack>
  );

  return (
    <Dialog
      size="sm"
      hasDivider
      open={open}
      title={title}
      content={content}
      onSecondaryActionClick={onClose}
      onPrimaryActionClick={saveCreative}
      primaryActionTitle="Format Bestätigen"
      isPrimaryActionDisabled={isUploadDisabled}
      isPrimaryActionLoading={isUploading}
      isSecondaryActionDisabled={isUploading}
    />
  );
};
